import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Sdílený výběr mezi navigačním stromem, canvasem a inspektorem (Epic 11,
 * kap. 8: "jedno sdílené selection state, ne dva nezávislé zdroje pravdy").
 * Skupiny a poznámky do stromu nepatří (epic-11 kap. 2) — sem se dostanou
 * jen z canvasu, proto pro ně žádný "focus" mechanismus neexistuje.
 */
export type InspectorTarget =
  | { kind: 'none' }
  | { kind: 'table'; tableId: string }
  | { kind: 'column'; tableId: string; columnId: string }
  | { kind: 'group'; groupId: string }
  | { kind: 'note'; noteId: string }
  /** `tableId` = vlastnící (referencující) tabulka, stejně jako adresují command factories (Epic 5). */
  | { kind: 'foreignKey'; tableId: string; foreignKeyId: string };

export type FocusTarget =
  | { kind: 'table'; tableId: string }
  | { kind: 'column'; tableId: string; columnId: string };

interface WorkspaceSelectionValue {
  /** Tabulky vybrané napříč stromem i canvasem — vstup pro "Seskupit" (E11-US4). */
  selectedTableIds: string[];
  inspectorTarget: InspectorTarget;
  /** Mění se jen `focusToken`; `CanvasView` na jeho změnu reaguje jednorázovým pan/zoom (E11-US3). */
  focusTarget: FocusTarget | null;
  focusToken: number;
  setSelectedTableIds: (tableIds: string[]) => void;
  setInspectorTarget: (target: InspectorTarget) => void;
  /** Klik na položku stromu: vybere, otevře v inspektoru A vyžádá si pan/zoom canvasu. */
  requestFocus: (target: FocusTarget) => void;
  clearSelection: () => void;
}

const WorkspaceSelectionContext = createContext<WorkspaceSelectionValue | null>(null);

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function WorkspaceSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedTableIds, setSelectedTableIdsState] = useState<string[]>([]);
  const [inspectorTarget, setInspectorTargetState] = useState<InspectorTarget>({ kind: 'none' });
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const [focusToken, setFocusToken] = useState(0);

  // No-op na obsahově stejné pole zabrání zbytečnému re-renderu při echu
  // z `CanvasView` (canvas -> context) po předchozí změně z tohoto směru.
  const setSelectedTableIds = useCallback((tableIds: string[]) => {
    setSelectedTableIdsState((previous) => (sameIds(previous, tableIds) ? previous : tableIds));
  }, []);

  const setInspectorTarget = useCallback((target: InspectorTarget) => {
    setInspectorTargetState(target);
  }, []);

  const requestFocus = useCallback((target: FocusTarget) => {
    setSelectedTableIdsState([target.tableId]);
    setInspectorTargetState(
      target.kind === 'table'
        ? { kind: 'table', tableId: target.tableId }
        : { kind: 'column', tableId: target.tableId, columnId: target.columnId },
    );
    setFocusTarget(target);
    setFocusToken((token) => token + 1);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTableIdsState([]);
    setInspectorTargetState({ kind: 'none' });
  }, []);

  const value = useMemo<WorkspaceSelectionValue>(
    () => ({
      selectedTableIds,
      inspectorTarget,
      focusTarget,
      focusToken,
      setSelectedTableIds,
      setInspectorTarget,
      requestFocus,
      clearSelection,
    }),
    [selectedTableIds, inspectorTarget, focusTarget, focusToken, setSelectedTableIds, setInspectorTarget, requestFocus, clearSelection],
  );

  return <WorkspaceSelectionContext.Provider value={value}>{children}</WorkspaceSelectionContext.Provider>;
}

export function useWorkspaceSelection(): WorkspaceSelectionValue {
  const context = useContext(WorkspaceSelectionContext);
  if (!context) throw new Error('useWorkspaceSelection musí být uvnitř WorkspaceSelectionProvider.');
  return context;
}
